import React, { useState, useEffect } from "react";
import {
  Table,
  Spinner,
  Row,
  Col,
  Input,
  Dropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
} from "reactstrap";
import { deviceDataAPI } from "../services/Device";
import TableHeader from "../components/TableHeader";

const subjectCache = {
  data: null,
  timestamp: null,
};

const SubjectList = () => {
  const [subjectList, setSubjectList] = useState([]);
  const [searchData, setSearchData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filterBy, setFilterBy] = useState("Site Name");
  const [searchValue, setSearchValue] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const [sortColumn, setSortColumn] = useState("");
  const [sortOrder, setSortOrder] = useState("");

  const [currentPage, setCurrentPage] = useState(1);
  const recordsPerPage = 50;

  const indexOfLastRecord = currentPage * recordsPerPage;
  const indexOfFirstRecord = indexOfLastRecord - recordsPerPage;
  const currentRecords = searchData.slice(indexOfFirstRecord, indexOfLastRecord);
  const totalPages = Math.ceil(searchData.length / recordsPerPage);

  const handleToggle = () => setIsOpen(!isOpen);

  const refreshlist = () => {
    setLoading(true);
    document.getElementById("refreshbtn").className = "fas fa-sync fa-spin";
    subjectCache.data = null;
    getAllDevice(false, true);
  };

  const applyFilters = () => {
    if (subjectList.length === 0) return;
    let filteredList = subjectList;
    if (searchValue) {
      filteredList = subjectList?.filter((item) => {
        if (filterBy === "Site Name") {
          return item?.clinicName?.toLowerCase()?.includes(searchValue.toLowerCase());
        } else if (filterBy === "Activation Date") {
          return timeFormatMinSec(item?.createdAt)?.includes(searchValue);
        }
      });
    }
    setSearchData(filteredList);
  };

  const handleFilterByChange = (value) => {
    setSearchValue("");
    setFilterBy(value);
  };

  const handleSearchInputChange = (e) => setSearchValue(e.target.value);

  useEffect(() => {
    applyFilters();
  }, [filterBy, searchValue]);

  function downloadCSV(csv, filename) {
    var csvFile = new Blob([csv], { type: "text/csv" });
    var downloadLink = document.createElement("a");
    downloadLink.download = filename;
    downloadLink.href = window.URL.createObjectURL(csvFile);
    downloadLink.style.display = "none";
    document.body.appendChild(downloadLink);
    downloadLink.click();
  }

  function exportTableToCSV() {
    let csv = [];
    let rows = document.querySelectorAll("table tr");
    for (let i = 0; i < rows.length; i++) {
      let row = [], cols = rows[i].querySelectorAll("td, th");
      for (let j = 0; j < cols.length; j++) row.push(cols[j].innerText);
      csv.push(row.join(","));
    }
    downloadCSV(csv.join("\n"), "SubjectList");
  }

  const getAllDevice = async (isBackground = false, isMounted = true) => {
    try {
      if (!isBackground) setLoading(true);
      const data = await deviceDataAPI();
      if (!isMounted) return;
      if (data.success && Array.isArray(data.devices)) {
        subjectCache.data = data.devices;
        setSubjectList(data.devices);
        if (!isBackground) setSearchData(data.devices);
      } else {
        console.warn("No subjects found:", data);
        if (!isBackground) {
          setSubjectList([]);
          setSearchData([]);
        }
      }
    } catch (error) {
      console.error("Failed to load subjects:", error);
      if (isMounted && !isBackground) {
        setSubjectList([]);
        setSearchData([]);
      }
    } finally {
      if (isMounted) {
        setLoading(false);
        const btn = document.getElementById("refreshbtn");
        if (btn) btn.className = "fas fa-sync fa-stop";
      }
    }
  };

  useEffect(() => {
    let isMounted = true;
    if (subjectCache.data) {
      setSubjectList(subjectCache.data);
      setSearchData(subjectCache.data);
      getAllDevice(true, isMounted);
    } else {
      getAllDevice(false, isMounted);
    }
    return () => { isMounted = false; };
  }, []);

  const timeFormatMinSec = (dateStr) => {
    if (!dateStr || dateStr == "undefined" || dateStr < 1520160669214) return " ";
    let date = new Date(parseInt(dateStr));
    let hours = date.getHours();
    let minutes = date.getMinutes();
    let ampm = hours >= 12 ? "PM" : "AM";
    hours = hours % 12 || 12;
    minutes = minutes < 10 ? "0" + minutes : minutes;
    let strTime = (" 0" + hours).slice(-2) + ":" + ("0" + minutes).slice(-2) + " " + ampm;
    let newDate = new Date(parseInt(dateStr)).toLocaleDateString("en-US", { year: "numeric", month: "2-digit", day: "2-digit" });
    return newDate + " (" + strTime + ")";
  };

  const calculateDuration = (startDate, endDate) => {
    if (!startDate || !endDate) return "-";
    const durationInMilliseconds = new Date(endDate) - new Date(startDate);
    return (durationInMilliseconds / (1000 * 60 * 60 * 24)).toFixed(0);
  };

  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortColumn(column);
      setSortOrder("asc");
    }
  };

  const deviceListRecords = () => {
    let sortedList = [...currentRecords];
    if (sortColumn) {
      sortedList.sort((a, b) => {
        if (sortOrder === "asc") return a[sortColumn]?.toString()?.localeCompare(b[sortColumn]);
        else return b[sortColumn]?.toString()?.localeCompare(a[sortColumn]);
      });
    }
    if (sortedList?.length > 0 && !loading) {
      return sortedList.map((item, index) => (
        <tr key={index}>
          <td style={{ textAlign: "left" }}>{item?.subjectId || "-"}</td>
          <td style={{ textAlign: "left" }}>
            <a href={"/#/device/" + item?.id}>{item?.prcId || "-"}</a>
          </td>
          <td style={{ textAlign: "left" }}>{item?.clinicName || "-"}</td>
          <td style={{ textAlign: "left" }}>{timeFormatMinSec(item?.createdAt) || "-"}</td>
          <td style={{ textAlign: "left" }}>{timeFormatMinSec(item?.updatedAt) || "-"}</td>
          <td style={{ textAlign: "left" }}>{calculateDuration(item?.createdAt, item?.updatedAt) || "-"} Days</td>
        </tr>
      ));
    } else {
      return (
        <>
         {loading ? (
  <tr>
    <td colSpan="6" style={{ textAlign: "center", padding: "60px 0" }}>
      <Spinner animation="border" style={{ width: "2.5rem", height: "2.5rem", color: "#525f7f" }} />
      <div style={{ marginTop: 12, color: "#6c757d", fontSize: 14 }}>Loading...</div>
    </td>
  </tr>
) : (
  <tr>
    <td colSpan="6" style={{ textAlign: "center", padding: "60px 0", color: "#6c757d" }}>No Record Found!</td>
  </tr>
)}
        </>
      );
    }
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", background: "#fff", height: "100vh", overflow: "hidden" }}>
      <TableHeader
        title="Sync Start and End Date"
        onRefresh={refreshlist}
        onDownload={exportTableToCSV}
        totalCount={searchData.length > 0 ? searchData.length : null}
  hasMore={false}
        filterContent={
          <Row>
            <Col md="0">
              <Dropdown isOpen={isOpen} toggle={handleToggle}>
                <DropdownToggle caret>Filter By: {filterBy}</DropdownToggle>
                <DropdownMenu>
                  <DropdownItem onClick={() => handleFilterByChange("Site Name")}>Site Name</DropdownItem>
                  <DropdownItem onClick={() => handleFilterByChange("Activation Date")}>Activation Date</DropdownItem>
                </DropdownMenu>
              </Dropdown>
            </Col>
            <Col md="3">
              <Input
                type="text"
                placeholder={`Enter ${filterBy}`}
                value={searchValue}
                onChange={handleSearchInputChange}
              />
            </Col>
          </Row>
        }
      />

      <div style={{ overflowY: "auto", background: "#fff" }}>
        <Table striped hover responsive bordered className="align-items-center table-flush" style={{ margin: 0 }}>
          <thead className="thead-light" style={{ cursor: "pointer", position: "sticky", top: 0, zIndex: 1 }}>
            <tr>
              <th scope="col" style={{ textAlign: "left" }} onClick={() => handleSort("subjectId")}>Subject Id</th>
              <th scope="col" style={{ textAlign: "left" }} onClick={() => handleSort("prcId")}>Ring Id</th>
              <th scope="col" style={{ textAlign: "left" }} onClick={() => handleSort("clinicName")}>Clinic Name</th>
              <th scope="col" style={{ textAlign: "left" }} onClick={() => handleSort("createdAt")}>Start Date (Activation)</th>
              <th scope="col" style={{ textAlign: "left" }} onClick={() => handleSort("updatedAt")}>Last Sync Date</th>
              <th scope="col" style={{ textAlign: "left" }}>Total Days</th>
            </tr>
          </thead>
          <tbody>
            {deviceListRecords()}
            {searchData.length > 50 && !loading && (
              <tr>
                <td colSpan="6" style={{ textAlign: "right", padding: "12px 16px" }}>
                  <div style={{ display: "inline-flex", gap: 8, alignItems: "center" }}>
                    <button
                      disabled={currentPage === 1}
                      onClick={() => setCurrentPage(prev => prev - 1)}
                      style={{
                        padding: "6px 14px", borderRadius: 6, border: "1px solid #dee2e6",
                        background: currentPage === 1 ? "#f8f9fa" : "#fff",
                        cursor: currentPage === 1 ? "not-allowed" : "pointer", fontWeight: 500,
                      }}
                    >←</button>
                   <div style={{ padding: "6px 14px", borderRadius: 6, background: "#f2f0f0", color: "#0e0d0d", fontWeight: 500, fontSize: 14 }}>
  {indexOfFirstRecord + 1}–{Math.min(indexOfLastRecord, searchData.length)} of {searchData.length}
</div>
                    <button
                      disabled={currentPage === totalPages || totalPages === 0}
                      onClick={() => setCurrentPage(prev => prev + 1)}
                      style={{
                        padding: "6px 14px", borderRadius: 6, border: "1px solid #dee2e6",
                        background: currentPage === totalPages ? "#f8f9fa" : "#fff",
                        cursor: currentPage === totalPages ? "not-allowed" : "pointer", fontWeight: 500,
                      }}
                    >→</button>
                  </div>
                </td>
              </tr>
            )}
          </tbody>
        </Table>
      </div>
    </div>
  );
};

export default SubjectList;